import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { usePackageProfileAttractionsOptional } from "../../contexts/PackageProfileAttractionsContext";
import {
  buildAttractionsSummaryViewModel,
  buildPackageAttractionsViewModel,
  resolveAdultChildUnitPricesIls,
} from "../../utils/attractions";
import { dynamicPackageAttractionLineBillableToCustomer } from "../../utils/packageAttractionRulesDisplay";
import PackageAttractionDetailModal from "./PackageAttractionDetailModal";
import PackageOptionalAttractionCard from "./PackageOptionalAttractionCard";
import "./PackageProfileAttractionsUnifiedBlock.scss";

/** @param {Record<string, unknown>} item */
function buildPackageAttractionDetailPayload(item, rule, line) {
  if (!item || typeof item !== "object") return null;
  const raw = item.raw && typeof item.raw === "object" ? item.raw : {};
  const cat = raw.catalog && typeof raw.catalog === "object" ? raw.catalog : {};
  const { adultPrice, childPrice, childEligible } = resolveAdultChildUnitPricesIls(
    rule,
    line
  );
  const descLong = String(raw.description || cat.description || "").trim();
  const dest = String(
    cat.destination_name ||
      cat.destination_label ||
      cat.area_label ||
      raw.destination_name ||
      ""
  ).trim();

  return {
    title: item.name,
    description: descLong || String(item.descriptionShort || "").trim(),
    descriptionShort: item.descriptionShort,
    adultPrice,
    childPrice,
    childEligible,
    category: item.category,
    durationMinutes: item.durationMinutes,
    destinationLabel: dest,
    imageUrl: cat.image_url || raw.image_url || null,
    raw: item.raw,
    item,
  };
}

function deriveInitialQuantities(lines) {
  /** @type {Record<string, { adults: number, children: number }>} */
  const out = {};
  for (const row of lines || []) {
    if (!dynamicPackageAttractionLineBillableToCustomer(row)) continue;
    const aid = row?.attraction_id;
    if (aid == null || Number.isNaN(Number(aid))) continue;
    out[String(Number(aid))] = { adults: 1, children: 0 };
  }
  return out;
}

/**
 * בלוק מאוחד: כלול + מתנות + אופציונליים (בחירת כמויות ב-FE).
 *
 * @param {{
 *   onFeStateChange?: (p: { feAddonUnpersistedIls: number, summaryVm: object }) => void,
 *   variant?: "full" | "includedOnly",
 * }} props
 * @remarks `includedOnly` — רק „מה כלול בחבילה” (כלול+מתנות), בלי אופציונלי ובלי דיווח סכומים ל-FE.
 */
export default function PackageProfileAttractionsUnifiedBlock({
  onFeStateChange,
  variant = "full",
}) {
  const includedOnly = variant === "includedOnly";
  const ctx = usePackageProfileAttractionsOptional();
  const [selectedQuantities, setSelectedQuantities] = useState(
    /** @type {Record<string, { adults: number, children: number }>} */ ({})
  );
  const didSeedOptional = useRef(false);

  const pkgId = ctx?.packageId ?? null;
  const attractionsContextMode = ctx?.attractionsContextMode ?? "none";
  const rulesLoading = ctx?.rulesLoading ?? false;
  const rulesError = ctx?.rulesError ?? null;
  const rulesForView = ctx?.rulesForView ?? [];
  const lines = ctx?.lines ?? [];
  const billableTotalGross = ctx?.billableTotalGross ?? 0;

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailPayload, setDetailPayload] = useState(null);

  const lineByAttractionId = useMemo(() => {
    const m = new Map();
    for (const row of lines) {
      const aid = row?.attraction_id;
      if (aid != null && !Number.isNaN(Number(aid))) m.set(Number(aid), row);
    }
    return m;
  }, [lines]);

  const ruleByAttractionId = useMemo(() => {
    const m = new Map();
    for (const r of rulesForView) {
      const aid = r?.attraction_id != null ? Number(r.attraction_id) : NaN;
      if (Number.isFinite(aid)) m.set(aid, r);
    }
    return m;
  }, [rulesForView]);

  const openAttractionDetail = useCallback(
    (item, rule) => {
      const aid = item?.id != null ? Number(item.id) : NaN;
      const line = Number.isFinite(aid) ? lineByAttractionId.get(aid) ?? null : null;
      setDetailPayload(buildPackageAttractionDetailPayload(item, rule, line));
      setDetailModalOpen(true);
    },
    [lineByAttractionId]
  );

  const closeAttractionDetail = useCallback(() => {
    setDetailModalOpen(false);
    setDetailPayload(null);
  }, []);

  useEffect(() => {
    didSeedOptional.current = false;
  }, [pkgId]);

  useEffect(() => {
    if (includedOnly) return;
    if (!ctx || rulesLoading) return;
    const seed = deriveInitialQuantities(lines);
    if (Object.keys(seed).length === 0) return;
    if (didSeedOptional.current) return;
    didSeedOptional.current = true;
    setSelectedQuantities((prev) => ({ ...seed, ...prev }));
  }, [includedOnly, ctx, rulesLoading, lines]);

  const setQtyForId = useCallback(
    (attractionId, next) => {
      const aid = Number(attractionId);
      const rule = ruleByAttractionId.get(aid);
      const line = lineByAttractionId.get(aid);
      const { childEligible: ce } = resolveAdultChildUnitPricesIls(rule, line);
      const id = String(attractionId);
      setSelectedQuantities((prev) => ({
        ...prev,
        [id]: {
          adults: Math.max(0, Math.floor(Number(next.adults) || 0)),
          children: ce ? Math.max(0, Math.floor(Number(next.children) || 0)) : 0,
        },
      }));
    },
    [ruleByAttractionId, lineByAttractionId]
  );

  const viewModel = useMemo(
    () =>
      buildPackageAttractionsViewModel({
        rules: rulesForView,
        lines,
        serverBillableTotalGross: billableTotalGross,
        selectedOptionalQuantities: selectedQuantities,
        currency: "ILS",
      }),
    [rulesForView, lines, billableTotalGross, selectedQuantities]
  );

  const summaryVm = useMemo(
    () => buildAttractionsSummaryViewModel(viewModel),
    [viewModel]
  );

  useEffect(() => {
    if (includedOnly) return;
    if (typeof onFeStateChange !== "function") return;
    onFeStateChange({
      feAddonUnpersistedIls: viewModel.pricing.feAddonUnpersistedIls,
      summaryVm,
    });
  }, [includedOnly, onFeStateChange, viewModel, summaryVm]);

  const hasIncluded = viewModel.includedAttractions.length > 0;
  const hasGifts = viewModel.giftAttractions.length > 0;
  const hasOptional = viewModel.optionalRows.length > 0;

  const showIncludedBlock = hasIncluded || hasGifts;
  const showOptionalBlock = hasOptional;

  const showBlock =
    rulesLoading || rulesError || showIncludedBlock || showOptionalBlock;

  if (!ctx) {
    if (includedOnly) return null;
    return (
      <div className="pkg-profile-attr-unified pkg-profile-attr-unified--placeholder" role="note">
        אטרקציות יוצגו לאחר טעינת העמוד.
      </div>
    );
  }

  if (attractionsContextMode === "none") {
    if (includedOnly) return null;
    return (
      <div className="pkg-profile-attr-unified pkg-profile-attr-unified--placeholder" role="note">
        אטרקציות יוצגו לאחר בחירת חבילה ויעד מתאימים לחיפוש.
      </div>
    );
  }

  if (!showBlock) {
    if (includedOnly) return null;
    return (
      <div className="pkg-profile-attr-unified pkg-profile-attr-unified--placeholder" role="status">
        אין אטרקציות להצגה לפי ההגדרות הנוכחיות.
      </div>
    );
  }

  if (includedOnly && rulesError) {
    return null;
  }

  if (includedOnly && !rulesLoading && !rulesError && !showIncludedBlock) {
    return null;
  }

  const rootClass = [
    "pkg-profile-attr-unified",
    "pkg-profile-attr-unified--standalone",
    includedOnly ? "pkg-profile-attr-unified--order-summary-included" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      aria-label={includedOnly ? "מה כלול בחבילה — אטרקציות" : "אטרקציות בחבילה"}
    >
      {!includedOnly ? (
        <header className="pkg-profile-attr-unified__header">
          <h2 className="pkg-profile-attr-unified__page-title">אטרקציות בחבילה</h2>
        </header>
      ) : null}

      {rulesLoading ? (
        <div className="pkg-profile-attr-unified__loading">
          <Spin size="small" />
        </div>
      ) : null}

      {rulesError && !includedOnly ? (
        <p className="pkg-profile-attr-unified__error" role="alert">
          {rulesError}
        </p>
      ) : null}

      {!rulesLoading && !rulesError && showIncludedBlock ? (
        <section className="pkg-profile-attr-unified__section pkg-profile-attr-unified__section--included-block" aria-labelledby="pkg-attr-included-heading">
          <h3 id="pkg-attr-included-heading" className="pkg-profile-attr-unified__title">
            מה כלול בחבילה
          </h3>
          <ul className="pkg-profile-attr-unified__included-list">
            {viewModel.includedAttractions.map((item) => (
              <li key={`inc-${item.id}`} className="pkg-profile-attr-unified__included-item">
                <span className="pkg-profile-attr-unified__pill pkg-profile-attr-unified__pill--included">
                  {item.packageRowStatusLabel || "כלול במחיר"}
                </span>
                <PackageOptionalAttractionCard
                  item={item}
                  adultPrice={null}
                  childPrice={null}
                  currency="ILS"
                  selectedAdults={0}
                  selectedChildren={0}
                  subtotalIls={0}
                  showQuantityControls={false}
                  showPriceRow={false}
                  onQuantityChange={() => {}}
                  onOpenDetails={() =>
                    openAttractionDetail(item, ruleByAttractionId.get(Number(item.id)))
                  }
                  className="pkg-profile-attr-unified__attr-card"
                />
              </li>
            ))}
            {viewModel.giftAttractions.map((item) => (
              <li key={`gift-${item.id}`} className="pkg-profile-attr-unified__included-item">
                <span
                  className={`pkg-profile-attr-unified__pill pkg-profile-attr-unified__pill--gift${
                    item.packageRowStatusLabel === "מתנה" ? " pkg-profile-attr-unified__pill--present" : ""
                  }`}
                >
                  {item.packageRowStatusLabel || "הטבה"}
                </span>
                <PackageOptionalAttractionCard
                  item={item}
                  adultPrice={null}
                  childPrice={null}
                  currency="ILS"
                  selectedAdults={0}
                  selectedChildren={0}
                  subtotalIls={0}
                  showQuantityControls={false}
                  showPriceRow={false}
                  onQuantityChange={() => {}}
                  onOpenDetails={() =>
                    openAttractionDetail(item, ruleByAttractionId.get(Number(item.id)))
                  }
                  className="pkg-profile-attr-unified__attr-card"
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!includedOnly && !rulesLoading && !rulesError && showOptionalBlock ? (
        <section
          className="pkg-profile-attr-unified__section pkg-profile-attr-unified__section--optional"
          aria-labelledby="pkg-attr-optional-heading"
        >
          <h3 id="pkg-attr-optional-heading" className="pkg-profile-attr-unified__title">
            אטרקציות נוספות שאפשר להוסיף
          </h3>
          <p className="pkg-profile-attr-unified__hint">
            בחירת כמויות לסיכום מחיר — ללא שמירה לשרת בשלב זה (למעט פריטים שכבר נוספו להזמנה).
          </p>
          <ul className="pkg-profile-attr-unified__optional-list">
            {viewModel.optionalRows.map((row) => {
              const id = row.item.id;
              return (
                <li key={`opt-${id}`} className="pkg-profile-attr-unified__optional-item">
                  <PackageOptionalAttractionCard
                    item={row.item}
                    adultPrice={row.adultPrice}
                    childPrice={row.childPrice}
                    childEligible={row.childEligible}
                    currency={row.currency}
                    selectedAdults={row.selectedAdults}
                    selectedChildren={row.selectedChildren}
                    subtotalIls={row.subtotalIls}
                    lineLocked={row.lineLocked}
                    onQuantityChange={(next) => setQtyForId(id, next)}
                    onOpenDetails={() => openAttractionDetail(row.item, row.rule)}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <PackageAttractionDetailModal
        open={detailModalOpen}
        onClose={closeAttractionDetail}
        payload={detailPayload}
      />
    </div>
  );
}
